import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Bell, Sun, Moon, ChevronDown, Circle, LogOut, User } from "lucide-react";
import { TOPBAR } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/auth/AuthProvider";

const CLUSTERS = [
  { id: "prod-us-east", label: "Production US-East", status: "healthy" },
  { id: "staging-eu-west", label: "Staging EU-West", status: "warning" },
  { id: "dev-local", label: "Development Local", status: "critical" },
];

const statusColor = { healthy: "text-emerald-400", warning: "text-amber-400", critical: "text-rose-400" };

const PAGE_TITLES = {
  "/dashboard": "Dashboard", "/clusters": "Clusters", "/topology": "Topology Map",
  "/explorer": "Resource Explorer", "/monitoring": "Monitoring", "/analytics": "Analytics",
  "/predictive": "Predictive Analytics", "/incidents": "Incidents", "/cost": "Cost Intelligence",
  "/assistant": "AI Assistant", "/settings": "Settings",
  "/connect": "Connect Cluster", "/manifest-analyzer": "Manifest Analyzer",
};

export default function TopBar({ clusterId, setClusterId, onSearchOpen }) {
  const [dark, setDark] = useState(true);
  const [clusterOpen, setClusterOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const title = PAGE_TITLES[location.pathname] || "KubeAnalytics";
  const cluster = CLUSTERS.find((c) => c.id === clusterId) || CLUSTERS[0];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleTheme = () => {
    setDark(!dark);
    document.documentElement.classList.toggle("dark");
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "U";

  return (
    <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-800 bg-slate-900/40 flex-shrink-0">
      <h1 className="text-base font-semibold text-slate-50 tracking-tight" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        {title}
      </h1>

      <div className="flex items-center gap-2">
        {/* Search */}
        <button
          data-testid={TOPBAR.searchBtn}
          onClick={onSearchOpen}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-slate-200 text-xs transition-colors"
        >
          <Search size={12} />
          <span className="hidden sm:block">Search...</span>
          <kbd className="hidden sm:block text-slate-600 text-[10px] font-mono border border-slate-700 rounded px-1">⌘K</kbd>
        </button>

        {/* Cluster Selector */}
        <div className="relative">
          <button
            data-testid={TOPBAR.clusterSelector}
            onClick={() => { setClusterOpen(!clusterOpen); setUserOpen(false); }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-700 bg-slate-800/80 text-xs text-slate-300 hover:text-slate-100 transition-colors"
          >
            <Circle size={6} className={cn("fill-current", statusColor[cluster.status])} />
            <span className="hidden sm:block max-w-[140px] truncate">{cluster.label}</span>
            <ChevronDown size={11} />
          </button>
          {clusterOpen && (
            <div className="absolute right-0 mt-1 w-52 z-50 rounded-md border border-slate-700 bg-slate-900 shadow-xl">
              {CLUSTERS.map((c) => (
                <button key={c.id} onClick={() => { setClusterId(c.id); setClusterOpen(false); }}
                  className={cn("flex items-center gap-2 w-full px-3 py-2 text-xs text-left hover:bg-slate-800 transition-colors",
                    c.id === clusterId ? "text-indigo-400" : "text-slate-300")}>
                  <Circle size={6} className={cn("fill-current flex-shrink-0", statusColor[c.status])} />
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Alerts */}
        <button data-testid={TOPBAR.notificationBtn} className="relative p-1.5 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
          <Bell size={16} strokeWidth={1.75} />
          <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-rose-400 rounded-full" />
        </button>

        {/* Theme toggle */}
        <button
          data-testid={TOPBAR.themeToggle}
          onClick={toggleTheme}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
        >
          {dark ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
        </button>

        {/* User Avatar */}
        {user && (
          <div className="relative">
            <button
              data-testid="topbar-user-menu"
              onClick={() => { setUserOpen(!userOpen); setClusterOpen(false); }}
              className="flex items-center gap-2 pl-2 pr-2.5 py-1 rounded-md border border-slate-700 bg-slate-800/80 hover:bg-slate-800 transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-[10px] font-semibold text-white">
                  {initials}
                </div>
              )}
              <span className="hidden sm:block text-xs text-slate-300 max-w-[100px] truncate">{user.name}</span>
              <ChevronDown size={11} className="text-slate-500" />
            </button>

            {userOpen && (
              <div className="absolute right-0 mt-1 w-52 z-50 rounded-md border border-slate-700 bg-slate-900 shadow-xl overflow-hidden">
                <div className="px-3 py-2.5 border-b border-slate-800">
                  <p className="text-xs font-semibold text-slate-200 truncate">{user.name}</p>
                  <p className="text-[10px] text-slate-500 truncate mt-0.5">{user.email}</p>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-xs text-rose-400 hover:bg-rose-400/10 transition-colors"
                >
                  <LogOut size={12} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
